import React, { useMemo, useState } from "react";
import { useI18n } from "@/i18n/I18nProvider";
import USMap, { STATE_UNIVERSITIES, US_STATES_TILES } from "@/components/map/USMap";
import { Search } from "lucide-react";

const stateNameOf = (abbr) => US_STATES_TILES.find((s) => s[1] === abbr)?.[0] || "";

const ALL_ROWS = Object.entries(STATE_UNIVERSITIES).flatMap(([abbr, list]) =>
  list.map((u) => ({ ...u, abbr, state: stateNameOf(abbr) || abbr }))
);

const matchesQuery = (u, q) =>
  !q || [u.name, u.state, u.div].some((v) => v.toLowerCase().includes(q));

const Universities = () => {
  const { t } = useI18n();
  const [selected, setSelected] = useState("CA");
  const [filter, setFilter] = useState("all");
  const [query, setQuery] = useState("");

  const stateName = stateNameOf(selected);

  const filteredAll = useMemo(() => {
    const q = query.trim().toLowerCase();
    return ALL_ROWS.filter((u) => (filter === "all" || u.div === filter) && matchesQuery(u, q));
  }, [filter, query]);

  const stateUniversities = useMemo(() => {
    const list = STATE_UNIVERSITIES[selected] || [];
    return list.filter((u) => filter === "all" || u.div === filter);
  }, [selected, filter]);

  return (
    <>
      <section className="bg-[#0F1B2D] text-white pt-36 pb-20 relative grain">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="overline text-[#C4A46A]">{t.universities.kicker}</div>
          <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight">
            {t.universities.title}
          </h1>
          <span className="gold-rule mt-8 block" />
        </div>
      </section>

      <section className="bg-[#F4F0E8]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-8">
              <div className="flex flex-wrap gap-2 mb-6" data-testid="division-filters">
                <button
                  onClick={() => setFilter("all")}
                  data-testid="filter-all"
                  className={`px-4 py-2 text-[11px] tracking-[0.24em] uppercase font-bold transition-colors border ${
                    filter === "all"
                      ? "bg-[#0F1B2D] text-[#C4A46A] border-[#0F1B2D]"
                      : "border-[#0F1B2D]/30 text-[#0F1B2D] hover:bg-[#0F1B2D]/5"
                  }`}
                >
                  {t.universities.filterAll}
                </button>
                {t.universities.categories.map((c) => {
                  // Normalize: use English division labels for filtering against data
                  const normalized = c.replace("Divisão", "Division");
                  return (
                    <button
                      key={c}
                      onClick={() => setFilter(normalized)}
                      data-testid={`filter-${c}`}
                      className={`px-4 py-2 text-[11px] tracking-[0.24em] uppercase font-bold transition-colors border ${
                        filter === normalized
                          ? "bg-[#0F1B2D] text-[#C4A46A] border-[#0F1B2D]"
                          : "border-[#0F1B2D]/30 text-[#0F1B2D] hover:bg-[#0F1B2D]/5"
                      }`}
                    >
                      {c}
                    </button>
                  );
                })}
              </div>

              <USMap selected={selected} onSelect={setSelected} filterDiv={filter} />
              <p className="mt-4 text-[#5E6470] text-sm">{t.universities.hintMap}</p>
            </div>

            <aside className="lg:col-span-4 bg-[#0F1B2D] text-white p-8 lg:sticky lg:top-28">
              <div className="overline text-[#C4A46A]">State</div>
              <h2 className="mt-3 font-display text-3xl tracking-tight" data-testid="selected-state">
                {stateName || "—"}
              </h2>
              <span className="gold-rule mt-4 block" />
              <ul className="mt-6 space-y-4" data-testid="state-universities">
                {stateUniversities.length === 0 && (
                  <li className="text-white/60 text-sm">{t.universities.noResults}</li>
                )}
                {stateUniversities.map((u) => (
                  <li key={`${u.name}-${u.div}`} className="border-t border-white/10 pt-4">
                    <div className="font-display text-white text-lg tracking-tight">{u.name}</div>
                    <div className="text-[#C4A46A] text-[11px] tracking-[0.24em] uppercase font-bold mt-1">
                      {u.div}
                    </div>
                  </li>
                ))}
              </ul>
            </aside>
          </div>

          {/* Search & complete list */}
          <div className="mt-24">
            <div className="flex items-center gap-3 border-b border-[#0F1B2D]/20 pb-3 max-w-2xl">
              <Search size={18} className="text-[#5E6470]" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t.universities.searchPlaceholder}
                data-testid="university-search"
                className="flex-1 bg-transparent border-none outline-none text-[#0F1B2D] placeholder:text-[#5E6470] py-2 text-lg"
              />
            </div>

            <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#0F1B2D]/10" data-testid="universities-grid">
              {filteredAll.length === 0 && (
                <div className="bg-[#F4F0E8] p-10 text-[#5E6470]">{t.universities.noResults}</div>
              )}
              {filteredAll.map((u, i) => (
                <div key={`${u.abbr}-${u.name}`} className="bg-[#F4F0E8] p-8" data-testid={`university-${i}`}>
                  <div className="overline">{u.state}</div>
                  <div className="mt-3 font-display text-lg lg:text-xl text-[#0F1B2D] tracking-tight">
                    {u.name}
                  </div>
                  <div className="mt-3 text-[#C4A46A] text-[11px] tracking-[0.24em] uppercase font-bold">
                    {u.div}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Universities;
